<template>
  <div>
    <h5 class="mb-3">Gateway Pagamento</h5>
    <v-alert v-if="error" type="error" class="mb-3">
      {{ error }}
    </v-alert>

    <template v-if="loading">
      <v-progress-linear
        :indeterminate="true"
        class="rounded-b-0"
        color="primary"
        rounded
      ></v-progress-linear>
      <v-alert text type="info" class="mb-3 rounded-t-0">
        Carregando configurações...
      </v-alert>
    </template>

    <v-expansion-panels>
      <v-expansion-panel v-for="gateway in gatewaysList" :key="gateway.id">
        <v-expansion-panel-header
          :disabled="gateway.underDevelopment"
          :expand-icon="gateway.underDevelopment ? null : undefined"
        >
          <h6 class="mb-0">{{ gateway.name }}</h6>
          <v-spacer />
          <div class="flex-grow-0 mr-1">
            <v-chip
              v-if="gateway.underDevelopment"
              x-small
              color="warning"
              label
            >
              Em desenvolvimento
            </v-chip>
            <v-chip v-else-if="gateway.valid" x-small color="success" label>
              Ativo
            </v-chip>
            <v-chip v-else x-small color="error" label> Inativo </v-chip>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <b>Compativel com:</b>
          <div class="d-flex flex-wrap gap-1 mb-4">
            <v-chip
              v-if="gateway.compatible?.theeds"
              small
              color="success"
              label
            >
              Autenticação 3DS
            </v-chip>
            <v-chip v-if="gateway.compatible?.pix" small color="primary" label>
              Pix
            </v-chip>
            <v-chip
              v-if="gateway.compatible?.creditCard"
              small
              color="primary"
              label
            >
              Cartão de Crédito
            </v-chip>
          </div>

          <component
            v-if="gateway.component"
            :is="gateway.component"
            :gateways="gateways"
            :platform="platform"
            @update:valid="updateValid(gateway.id, $event)"
          />
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <GatewayRules
      :loading="loading"
      :gateways="gateways"
      :gatewaysList="gatewaysList"
      :platform="platform"
      :rules="rules"
      @rules-cleared="rules = []"
    />
  </div>
</template>

<script>
import forge from "node-forge";
import moment from "moment";

import platformService from "@/services/admin/platform";
import PagseguroSettings from "./gatewaySettings/PagseguroSettings.vue";
import CieloSettings from "./gatewaySettings/CieloSettings.vue";
import PagarmeSettings from "./gatewaySettings/PagarmeSettings.vue";
import InterSettings from "./gatewaySettings/InterSettings.vue";
import MercadoPagoSettings from "./gatewaySettings/MercadoPagoSettings.vue";
import GatewayRules from "./gatewaySettings/GatewayRules.vue";

const interIsValid = (gateways) => {
  if (!gateways.inter) return false;
  if (!gateways.inter.certificate) return false;
  const cert = forge.pki.certificateFromPem(gateways.inter.certificate);
  const expirationDate = cert.validity.notAfter;
  return (
    !!gateways.inter.clientId && !moment(expirationDate).isBefore(moment())
  );
};

export default {
  name: "PlatformGateway",
  components: {
    PagseguroSettings,
    CieloSettings,
    PagarmeSettings,
    InterSettings,
    MercadoPagoSettings,
    GatewayRules,
  },
  props: {
    platform: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      loading: false,
      loadingSaveGateway: false,
      errorSaveGateway: null,
      selectedGateway: null,
      gateways: {},
      rules: [],
      gatewaysList: [
        {
          id: "PAGSEGURO",
          name: "PagSeguro",
          valid: false,
          compatible: {
            theeds: true,
            pix: true,
            creditCard: true,
          },
          isValid: (gateways) => !!gateways?.pagseguro?.publicKey,
          component: PagseguroSettings,
        },
        {
          id: "CIELO",
          name: "Cielo",
          valid: false,
          compatible: {
            theeds: true,
            pix: true,
            creditCard: true,
          },
          isValid: (gateways) => !!gateways?.cielo?.merchantId,
          component: CieloSettings,
        },
        {
          id: "PAGARME",
          name: "PagarMe",
          valid: false,
          compatible: {
            theeds: false,
            pix: true,
            creditCard: true,
          },
          isValid: (gateways) =>
            !!gateways?.pagarme?.secret && !!gateways?.pagarme?.publicKey,
          component: PagarmeSettings,
        },
        {
          id: "INTER",
          name: "Inter",
          underDevelopment: false,
          valid: false,
          compatible: {
            theeds: false,
            pix: true,
            creditCard: false,
          },
          isValid: interIsValid,
          component: InterSettings,
        },
        {
          id: "MERCADOPAGO",
          name: "Mercado Pago",
          supportsRouting: false,
          valid: false,
          compatible: {
            theeds: false,
            pix: true,
            creditCard: true,
          },
          isValid: (gateways) =>
            !!gateways?.mercadopago?.client_id &&
            !!gateways?.mercadopago?.client_secret &&
            !!gateways?.mercadopago?.access_token,
          component: MercadoPagoSettings,
        },
        // {
        //   id: "PAL.CO",
        //   name: "Pal.co",
        //   underDevelopment: true,
        //   valid: false,
        //   compatible: {
        //     theeds: false,
        //     pix: true,
        //     creditCard: false,
        //   },
        // },
      ],
      error: null,
      loading: false,
    };
  },
  methods: {
    async getGateways() {
      try {
        this.loading = true;
        const response = await platformService.getGateways(this.platform.id);

        this.rules = response.rules;
        this.gateways = response.gateways;
        this.gatewaysList.forEach((gateway) => {
          if (gateway.isValid) gateway.valid = gateway.isValid(this.gateways);
        });
      } catch (error) {
        this.error = error.message || "Erro ao buscar gateways";
      } finally {
        this.loading = false;
      }
    },
    updateValid(id, valid) {
      const index = this.gatewaysList.findIndex((gateway) => gateway.id === id);
      this.gatewaysList[index].valid = valid;
    },
  },
  mounted() {
    this.getGateways();
  },
};
</script>
